import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import useLocales from '../../hooks/useLocales';
import { RootState, useSelector } from '../../redux/store';
import getOrganizationDisplayName from '../../utils/getOrganizationDisplayName';
import type { OrganizationType } from '../../types/organization';

interface SelectOrganizationProps {
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  isClearable?: boolean;
  selected?: OrganizationType | null;
  optionsFilter?: (options: OrganizationType[]) => OrganizationType[];
  onChange?: (organizations: OrganizationType) => void;
}

/**
 * 組織を選択するコンポーネント。OrganizationTypeを引数に取るコールバックがあれば実行する。
 * @param id? [string]
 * @param className? [string]
 * @param disabled? [boolean]
 * @param isClearable? [boolean]
 * @param selected [OptionsType | null]
 * @param optionsFilter?: (options: OrganizationType[]) => OrganizationType[];
 * @param onChange?: (organizations: OrganizationType) => void;
 * @returns React.ReactElement
 */
const SelectOrganization = ({
  id = 'selectOrganizations',
  className = '',
  disabled = false,
  isClearable = false,
  selected = null,
  optionsFilter = (options) => options,
  onChange = () => {},
}: SelectOrganizationProps): React.ReactElement => {
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { organizations } = useSelector((state: RootState) => state.organization);
  const { organizationBase } = useSelector((state: RootState) => state.options);
  const [options, setOptions] = useState<OrganizationType[]>(organizations ?? []);
  const [selectedOption, setSelectedOption] = useState<OrganizationType | null>(selected ?? null);
  const getOptionLabel = (option: OrganizationType | null) => (option ? getOrganizationDisplayName(option, { organizationBases: organizationBase, language: currentLang.value }) : '');
  const getOptionValue = (option: OrganizationType | null) => (option ? String(option.id) : null);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  useEffect(() => {
    const filteredOrganizations: OrganizationType[] = optionsFilter(organizations);
    setOptions(filteredOrganizations);
  }, [organizations]);

  // EVENT HANDLER
  // ================================================================================

  // プルダウンから組織を選んだとき
  const handleSelectChange = (option: OrganizationType | null) => {
    setSelectedOption(option);
    onChange(option);
  };

  // VIEW
  // ================================================================================

  return (
    <Select
      inputId={id}
      isMulti={false}
      options={options}
      value={selectedOption}
      onChange={handleSelectChange}
      isDisabled={disabled}
      isClearable={isClearable}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={t('dropdown.organization')}
      className={'react-select-container' + ' ' + className}
      classNamePrefix="react-select"
    />
  );
};

export default SelectOrganization;
